import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { BUG_REPORT_ATTACHMENT_ACCEPT } from "@/lib/bugReportScreenshots";
import { Paperclip, X } from "lucide-react";

type ProductFeedbackMode = "bug" | "feature";

interface ProductFeedbackDialogProps {
  open: boolean;
  mode: ProductFeedbackMode;
  summary: string;
  details: string;
  isSubmitting?: boolean;
  submissionSucceeded?: boolean;
  onOpenChange: (open: boolean) => void;
  onSummaryChange: (value: string) => void;
  onDetailsChange: (value: string) => void;
  onSubmit: () => void | Promise<void>;
  onResetSuccessState?: () => void;
  attachmentFileName?: string | null;
  onAttachmentChange?: (file: File | null) => void;
  onAttachmentRemove?: () => void;
}

const dialogCopy = {
  bug: {
    title: "Report bug",
    description: "Tell us what broke and we will capture the surrounding context for review.",
    summaryLabel: "What broke?",
    summaryPlaceholder: "Short summary of the bug",
    detailsLabel: "Anything else we should know?",
    detailsPlaceholder: "Optional details that could help reproduce it",
  },
  feature: {
    title: "Suggest feature",
    description: "Share what would make the product stronger and we will capture it for GitHub review.",
    summaryLabel: "What would you like to see?",
    summaryPlaceholder: "Short summary of the idea",
    detailsLabel: "Why would this help?",
    detailsPlaceholder: "Optional context about the workflow or outcome you want",
  },
} as const;

export function ProductFeedbackDialog({
  open,
  mode,
  summary,
  details,
  isSubmitting = false,
  submissionSucceeded = false,
  onOpenChange,
  onSummaryChange,
  onDetailsChange,
  onSubmit,
  onResetSuccessState,
  attachmentFileName = null,
  onAttachmentChange,
  onAttachmentRemove,
}: ProductFeedbackDialogProps) {
  const copy = dialogCopy[mode];
  const isFeatureReceipt = mode === "feature" && submissionSucceeded;
  const showAttachmentInput = mode === "bug" && !isFeatureReceipt;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isFeatureReceipt ? "Feature request received" : copy.title}</DialogTitle>
          <DialogDescription>
            {isFeatureReceipt
              ? "We captured it for GitHub review."
              : copy.description}
          </DialogDescription>
        </DialogHeader>

        {isFeatureReceipt ? (
          <div className="space-y-4">
            <div className="rounded-2xl border border-primary/20 bg-primary/5 p-4">
              <p className="text-sm font-medium text-foreground">{summary.trim()}</p>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                You can close this now, or send another idea while the handoff is still fresh.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor={`product-feedback-summary-${mode}`}>{copy.summaryLabel}</Label>
              <Input
                id={`product-feedback-summary-${mode}`}
                value={summary}
                onChange={(event) => onSummaryChange(event.target.value)}
                placeholder={copy.summaryPlaceholder}
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor={`product-feedback-details-${mode}`}>{copy.detailsLabel}</Label>
              <Textarea
                id={`product-feedback-details-${mode}`}
                value={details}
                onChange={(event) => onDetailsChange(event.target.value)}
                placeholder={copy.detailsPlaceholder}
                disabled={isSubmitting}
              />
            </div>

            {showAttachmentInput && (
              <div className="rounded-lg border border-dashed border-border bg-muted/30 p-3">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-foreground">Attachment</p>
                  <p className="text-xs leading-5 text-muted-foreground">
                    Optional. Add a screenshot, TXT file, PDF, doc, or spreadsheet that helps us see what happened.
                  </p>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button type="button" variant="outline" size="sm" asChild disabled={isSubmitting}>
                    <label htmlFor="product-feedback-bug-attachment" className="cursor-pointer">
                      <Paperclip className="mr-2 h-4 w-4" />
                      Attach file
                    </label>
                  </Button>
                  <input
                    id="product-feedback-bug-attachment"
                    aria-label="Attach file"
                    type="file"
                    accept={BUG_REPORT_ATTACHMENT_ACCEPT}
                    className="sr-only"
                    disabled={isSubmitting}
                    onChange={(event) => onAttachmentChange?.(event.target.files?.[0] ?? null)}
                  />
                </div>
                {attachmentFileName && (
                  <div className="mt-3 flex items-center justify-between gap-3 rounded-md border bg-background px-3 py-2">
                    <a href="#" onClick={(event) => event.preventDefault()} className="truncate text-sm text-primary">
                      {attachmentFileName}
                    </a>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={onAttachmentRemove}
                      disabled={isSubmitting}
                      aria-label="Remove attachment"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {isFeatureReceipt ? (
            <>
              <Button type="button" variant="outline" onClick={() => onResetSuccessState?.()}>
                Suggest another feature
              </Button>
              <Button type="button" onClick={() => onOpenChange(false)}>
                Done
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="button" onClick={() => void onSubmit()} disabled={isSubmitting || !summary.trim()}>
                Send to Coach Veera
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
